import { Clock } from 'lucide-react';
import type { MaintenanceItem } from '@/models/entities/lead';

interface LeadMaintenanceItemProps {
  readonly item: MaintenanceItem;
}

export function LeadMaintenanceItem({ item }: LeadMaintenanceItemProps) {
  return (
    <section className="border border-gray-300 rounded-lg p-3 mb-2 shadow-sm">
      <div className="flex justify-between items-center mb-2 border-b border-gray-100">
        <h4 className="font-bold text-sm text-gray-900">{item.description}</h4>
        <dl className="flex items-center gap-2 text-[10px] bg-white border border-gray-200 px-1.5 py-0.5 rounded">
          <dt className="text-gray-500 font-bold tracking-tight uppercase">
            VMRS
          </dt>
          <dd className="font-mono font-semibold text-gray-900">
            {item.vmrsCode}
          </dd>
        </dl>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <section>
          <h5 className="text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-2">
            SRTs
          </h5>
          <ul className="space-y-2 text-xs list-none p-0">
            {item.srts.map((srt) => (
              <li
                key={srt.code}
                className="bg-white border border-gray-100 rounded-lg p-2.5 flex flex-col h-full"
              >
                <div className="flex justify-between items-start mb-1.5">
                  <h6 className="text-gray-900 font-semibold leading-tight">
                    {srt.description}
                  </h6>
                  <div className="flex items-center shrink-0 bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded ml-2">
                    <Clock className="w-3 h-3 mr-1" aria-hidden="true" />
                    <span className="font-bold">{srt.hours.toFixed(1)}</span>
                  </div>
                </div>
                <dl className="flex gap-4 mt-auto pt-1.5 border-t border-gray-50">
                  <div className="flex items-center gap-1.5 text-[10px]">
                    <dt className="text-gray-400 font-bold uppercase">CODE</dt>
                    <dd className="font-mono text-gray-600 font-medium">
                      {srt.code}
                    </dd>
                  </div>
                </dl>
              </li>
            ))}
          </ul>
        </section>

        <section className="col-span-2">
          <h5 className="text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-2">
            Parts
          </h5>
          <ul className="list-none p-0 grid grid-cols-2 gap-2">
            {item.parts.map((part) => (
              <li
                key={part.number}
                className="bg-white border border-gray-100 rounded-lg p-2.5 text-xs flex flex-col justify-between h-full"
              >
                <div className="mb-1.5">
                  <h6 className="text-gray-900 font-medium leading-tight">
                    {part.description}
                  </h6>
                </div>
                <dl className="flex items-end justify-between mt-auto pt-1.5 border-t border-gray-50">
                  <div className="flex items-center gap-1.5 text-[10px]">
                    <dt className="sr-only">
                      <abbr title="Part Number">PN</abbr>
                    </dt>
                    <dd className="font-mono text-gray-500">{part.number}</dd>
                  </div>
                  <div className="flex items-baseline gap-1 bg-gray-50 px-2 py-0.5 rounded border border-gray-100">
                    <dt className="text-[10px] text-gray-400 font-bold uppercase">
                      Qty
                    </dt>
                    <dd className="text-xs font-bold text-gray-900">
                      {part.quantity.value}
                    </dd>
                  </div>
                </dl>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </section>
  );
}
